/**
 * The AR → mosaic seam.
 *
 * AR ends its handoff with the mosaic still cover-filling the viewport. The
 * mosaic page then mounts a Leaflet map at its own cover zoom of the same
 * still. The images and the framing already agree; what the viewer sees is a
 * blank frame in between, because the map needs a tick to paint.
 *
 * So we carry the last cover frame across the navigation: AR stores the still
 * URL (same origin, already downloaded) in sessionStorage, and the mosaic page
 * paints it full-bleed on top until Leaflet has rendered, then fades it out.
 *
 * sessionStorage is per tab and survives a LiveView `push_navigate`, which is
 * a same-document patch. A fresh load with no entry renders nothing extra.
 */

export const STORAGE_KEY = "moon_scan:handoff"
export const TTL_MS = 15000

/**
 * The seam is only worth carrying when the URL is a real image and the entry
 * is recent. A stale entry from an abandoned session must not paint.
 */
export function parseSeam(raw, now = Date.now(), ttlMs = TTL_MS) {
  if (!raw) return null
  let entry
  try {
    entry = JSON.parse(raw)
  } catch (_err) {
    return null
  }
  if (!entry || typeof entry.url !== "string" || entry.url === "") return null
  if (!Number.isFinite(entry.at)) return null
  const age = now - entry.at
  if (age < 0 || age > ttlMs) return null
  return {url: entry.url, at: entry.at}
}

export function encodeSeam(url, now = Date.now()) {
  if (typeof url !== "string" || url === "") return null
  return JSON.stringify({url, at: now})
}

/** Write the seam entry. Silently no-ops when storage is unavailable. */
export function remember(storage, url, now = Date.now()) {
  const payload = encodeSeam(url, now)
  if (!payload) return false
  try {
    storage.setItem(STORAGE_KEY, payload)
    return true
  } catch (_err) {
    return false
  }
}

/**
 * Read and clear the entry: one handoff, one carry-over. Returns
 * `{url, at}` or null.
 */
export function take(storage, now = Date.now()) {
  let raw = null
  try {
    raw = storage.getItem(STORAGE_KEY)
    storage.removeItem(STORAGE_KEY)
  } catch (_err) {
    return null
  }
  return parseSeam(raw, now)
}

/**
 * Paint `url` over `container`, then fade it out once `map` is ready and the
 * image has decoded. Always removes itself: a stalled image must never keep a
 * frame above the map.
 *
 * Returns `{img, done}` — `done` resolves when the overlay is gone, so the
 * caller can hold its first camera move until the seam has cleared. Returns
 * null when there is nothing to paint.
 */
export function paintCarryOver({
  container,
  url,
  map,
  doc = document,
  win = window,
  fadeMs = 320,
  safetyMs = 1400,
}) {
  if (!container || !url) return null

  const reduceMotion = win.matchMedia("(prefers-reduced-motion: reduce)").matches
  const img = doc.createElement("img")
  img.className = "mosaic-seam"
  img.alt = ""
  img.decoding = "async"
  img.src = url

  container.appendChild(img)

  let removed = false
  let settleDone = () => {}
  const done = new Promise((resolve) => {
    settleDone = resolve
  })

  const remove = () => {
    if (removed) return
    removed = true
    if (img.parentNode) img.parentNode.removeChild(img)
    settleDone()
  }

  const fadeOut = () => {
    if (removed) return
    if (reduceMotion || fadeMs <= 0) {
      remove()
      return
    }
    img.classList.add("mosaic-seam--done")
    win.setTimeout(remove, fadeMs + 40)
  }

  // Two frames after the map is ready: one for Leaflet's first render, one so
  // the fade starts on a painted frame rather than mid-layout.
  const whenMapReady = () => {
    win.requestAnimationFrame(() => {
      win.requestAnimationFrame(fadeOut)
    })
  }

  if (map && typeof map.whenReady === "function") {
    map.whenReady(whenMapReady)
  } else {
    whenMapReady()
  }

  // The image may still be decoding when the map is ready; fading before it
  // can paint would show the bare map through the fade.
  if (typeof img.decode === "function") {
    img.decode().then(whenMapReady, () => {})
  }

  win.setTimeout(remove, safetyMs)

  return {img, done}
}

export default {STORAGE_KEY, TTL_MS, parseSeam, encodeSeam, remember, take, paintCarryOver}
